import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import { TrendingUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: '#F59E0B',
  confirmed: '#3B82F6',
  in_production: '#F97316',
  quality_check: '#8B5CF6',
  ready_for_delivery: '#14B8A6',
  out_for_delivery: '#6366F1',
  delivered: '#10B981',
  completed: '#64748B',
  ordered: '#0052FF'
};

const CATEGORY_COLORS = {
  window: '#0052FF',
  door: '#8B5CF6',
  partition: '#10B981'
};

const PIE_COLORS = ['#0052FF', '#8B5CF6', '#10B981', '#F59E0B', '#F97316', '#EF4444', '#14B8A6', '#6366F1'];

export default function AnalyticsDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/dashboard/analytics`, {
        withCredentials: true
      });
      setAnalytics(data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <p className="text-slate-500">Loading analytics...</p>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20">
        <p className="text-slate-500">No analytics data available yet. Create some orders to see charts.</p>
      </div>
    );
  }

  const hasMonthlyData = analytics.monthly_data?.length > 0;
  const hasOrderStatus = analytics.orders_by_status?.length > 0;
  const hasCategoryData = analytics.revenue_by_category?.length > 0;
  const hasTopProducts = analytics.top_products?.length > 0;

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border border-slate-200 rounded-sm p-3 shadow-sm">
          <p className="text-xs text-slate-500 mb-1 uppercase tracking-wider">{label}</p>
          {payload.map((p, i) => (
            <p key={i} className="text-sm font-medium" style={{ color: p.color }}>
              {p.name}: {p.name.toLowerCase().includes('revenue') ? `₹${p.value.toFixed(2)}` : p.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-6" data-testid="analytics-dashboard">
      <div className="flex items-center gap-2 mb-2">
        <TrendingUp size={24} className="text-[#0052FF]" />
        <h3 className="text-2xl font-medium text-slate-900">Analytics Overview</h3>
      </div>

      {/* Row 1: Revenue Chart + Orders by Status */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Over Time */}
        <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="revenue-chart">
          <CardHeader className="p-6">
            <CardTitle className="text-lg font-medium">Revenue Over Time</CardTitle>
            <CardDescription className="text-sm text-slate-500">Monthly revenue and order volume</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {hasMonthlyData ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.monthly_data} barGap={4}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="revenue" name="Revenue" fill="#0052FF" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm py-8 text-center">No revenue data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Orders by Status */}
        <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="orders-status-chart">
          <CardHeader className="p-6">
            <CardTitle className="text-lg font-medium">Orders by Status</CardTitle>
            <CardDescription className="text-sm text-slate-500">Distribution of current order statuses</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {hasOrderStatus ? (
              <ResponsiveContainer width="100%" height={260}>
                <PieChart>
                  <Pie
                    data={analytics.orders_by_status}
                    dataKey="count"
                    nameKey="status"
                    cx="50%"
                    cy="50%"
                    outerRadius={90}
                    innerRadius={50}
                    paddingAngle={3}
                    label={({ status, count }) => `${status.replace('_', ' ')} (${count})`}
                    labelLine={{ stroke: '#94A3B8', strokeWidth: 1 }}
                  >
                    {analytics.orders_by_status.map((entry, i) => (
                      <Cell key={i} fill={STATUS_COLORS[entry.status] || PIE_COLORS[i % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<CustomTooltip />} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm py-8 text-center">No order data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 2: Category Revenue + Top Products */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue by Category */}
        <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="category-revenue-chart">
          <CardHeader className="p-6">
            <CardTitle className="text-lg font-medium">Revenue by Category</CardTitle>
            <CardDescription className="text-sm text-slate-500">Windows, doors, and partitions breakdown</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {hasCategoryData ? (
              <ResponsiveContainer width="100%" height={260}>
                <BarChart data={analytics.revenue_by_category} layout="vertical" barSize={24}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={(v) => `₹${v}`} />
                  <YAxis type="category" dataKey="category" tick={{ fontSize: 12, fill: '#475569', textTransform: 'capitalize' }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar dataKey="revenue" name="Revenue" radius={[0, 4, 4, 0]}>
                    {analytics.revenue_by_category.map((entry, i) => (
                      <Cell key={i} fill={CATEGORY_COLORS[entry.category] || PIE_COLORS[i]} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <p className="text-slate-400 text-sm py-8 text-center">No category data yet</p>
            )}
          </CardContent>
        </Card>

        {/* Top Products */}
        <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="top-products-chart">
          <CardHeader className="p-6">
            <CardTitle className="text-lg font-medium">Top Products</CardTitle>
            <CardDescription className="text-sm text-slate-500">Most quoted products</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            {hasTopProducts ? (
              <div className="space-y-4">
                {analytics.top_products.map((product, i) => {
                  const maxQuotes = Math.max(...analytics.top_products.map(p => p.quotes));
                  const pct = maxQuotes > 0 ? (product.quotes / maxQuotes) * 100 : 0;
                  return (
                    <div key={i} data-testid={`top-product-${i}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-slate-900 truncate max-w-[200px]">{product.name}</span>
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border"
                            style={{
                              color: CATEGORY_COLORS[product.category] || '#475569',
                              borderColor: CATEGORY_COLORS[product.category] || '#CBD5E1',
                              backgroundColor: `${CATEGORY_COLORS[product.category] || '#475569'}10`
                            }}>
                            {product.category}
                          </span>
                          <span className="text-sm font-mono font-medium text-slate-700">{product.quotes}</span>
                        </div>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2">
                        <div
                          className="h-2 rounded-full transition-all duration-500"
                          style={{
                            width: `${pct}%`,
                            backgroundColor: CATEGORY_COLORS[product.category] || '#0052FF'
                          }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-slate-400 text-sm py-8 text-center">No product data yet</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Row 3: Order Volume Line Chart */}
      {hasMonthlyData && (
        <Card className="border border-slate-200 rounded-sm shadow-none" data-testid="order-volume-chart">
          <CardHeader className="p-6">
            <CardTitle className="text-lg font-medium">Order Volume Trend</CardTitle>
            <CardDescription className="text-sm text-slate-500">Number of orders per month</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0">
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={analytics.monthly_data}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={{ stroke: '#E2E8F0' }} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Line type="monotone" dataKey="orders" name="Orders" stroke="#0052FF" strokeWidth={2.5} dot={{ fill: '#0052FF', r: 4 }} activeDot={{ r: 6 }} />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
